import { useCallback } from 'react';

import type { ActionNode } from 'flow-builder/renderers/node/actionNodeComponent';
import { styles } from 'flow-builder/renderers/node/nodeStyles';
import type { Bounds } from 'flow-builder/renderers/node/nodeUtils';
import { getIsInBounds } from 'flow-builder/renderers/node/nodeUtils';
import type { EditorContext } from 'flow-builder/types/context';
import { getIsNodeInTreeForId } from 'flow-builder/utils/node/getNodeTree/getNodeTree';
import { getOrphans } from 'flow-builder/utils/node/getOrphans/getOrphans';

type UseActionNodeMoveOptions = Pick<EditorContext, 'state'>;
type UseActionNodeMoveReturn = (actionNode: ActionNode) => void;

export function useActionNodeMove({
  state,
}: UseActionNodeMoveOptions): UseActionNodeMoveReturn {
  const {
    nodeState: { relations },
    attachState,
    setAttachState,
  } = state;

  const onActionNodeMove = useCallback(
    (actionNode: ActionNode) => {
      const ref = actionNode.ref;
      const {
        node: { id: actionNodeId },
      } = actionNode.props;
      const { hoveredNodeId } = attachState;

      const targetPageX = ref.position.x + styles.base.width / 2;
      const targetPageY = ref.position.y;

      const orphans = getOrphans(relations);

      const newHoveredNode = orphans.find(({ id, height, pageX, pageY }) => {
        const bounds: Bounds = {
          x: [pageX, pageX + styles.base.width],
          y: [pageY, pageY + height],
        };

        return (
          getIsInBounds(targetPageX, targetPageY, bounds) &&
          !getIsNodeInTreeForId(id, actionNodeId, relations)
        );
      });

      if (newHoveredNode && hoveredNodeId !== newHoveredNode.id) {
        setAttachState({
          actionNodeId,
          hoveredNodeId: newHoveredNode.id,
          isDragging: true,
        });

        return;
      }

      if (hoveredNodeId && !newHoveredNode) {
        setAttachState({
          actionNodeId,
          hoveredNodeId: null,
          isDragging: true,
        });

        return;
      }
    },
    [relations, attachState, setAttachState],
  );

  return onActionNodeMove;
}
